import type { RunStatus } from '@ralph/contracts'
import clsx from 'clsx'
import {
  Activity,
  CircleCheck,
  CircleSlash,
  Clock,
  Coins,
  Minus,
  RotateCcw,
  TrendingDown,
  TrendingUp,
  Zap
} from 'lucide-react'
import { type CSSProperties, type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { AgentUsagePanel } from '@/components/activity/AgentUsagePanel.tsx'
import { StoragePanel } from '@/components/activity/StoragePanel.tsx'
// The one shared rounding rule for a rate in this product, with its own spec pinning the
// cases that matter here: a non-zero rate never renders as "0%". It lives in the maker plane
// only because that is where it was first needed.
import { formatRate } from '@/components/marketplace/maker/analytics-presentation.ts'
import { Badge, Button, EmptyState, Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useModels, useRuns, useSession, useStorage, useTools, useUsage } from '@/hooks/use-api.ts'
import { type I18n, useI18n } from '@/i18n/index.ts'

/**
 * Operator view: what the platform did, what it cost, and whether it worked.
 *
 * Reports task-shaped metrics next to token counts on purpose. Tokens answer "how much did
 * we spend"; successful runs and cost-per-successful-run answer "did the spend produce
 * anything", which is the question an owner actually has.
 *
 * Two things this screen refuses to do, because a dashboard that does them stops being
 * evidence:
 *
 *   - **It never draws a number it was not given.** Every value below names an endpoint
 *     field. There is no per-run groundedness anywhere in the API, so there is no
 *     groundedness badge on a run row; latency is computed from the two timestamps the run
 *     list carries and labelled as what that arithmetic actually measures.
 *   - **It never draws a trend it cannot see.** Deltas and sparklines exist only for the two
 *     series `/api/admin/usage` really returns (runs and cost per day). A metric without a
 *     series keeps its card and loses its chart, rather than gaining invented points.
 */
export const ActivityPage = () => {
  const session = useSession()
  // Before the gate rather than after it: a hook cannot be conditional, and the refusal
  // below is a sentence a person reads (PRD-27 LOC-012).
  const t = useI18n().t.activity

  const roles = session.data?.user?.roles ?? []
  const isOperator = roles.includes('operator') || roles.includes('admin')

  /*
   * The gate is a component boundary rather than an early return inside one body. It used to
   * be the latter, which meant `useRuns()` had already been called by the time the check ran
   * and `GET /api/runs` fired for any signed-in reader who typed the URL. Hooks cannot be
   * conditional; mounting is the only way to make a query conditional on a role.
   */
  if (!isOperator) {
    return <EmptyState title={t.operatorOnly.title} body={t.operatorOnly.body} />
  }

  return <OperatorDashboard />
}

/**
 * The three windows this screen offers.
 *
 * Numbers, not labelled pairs: the label was English copy sitting in a module-scope constant,
 * which is the shape F-3 is about even before a catalogue is involved. It is
 * `t.rangeDays(…)` now, read inside the body, and what is left here cannot be frozen by an
 * import because it is not a word (PRD-27 D-25).
 */
const RANGE_DAYS = [7, 30, 90] as const

/** One bucket of `GET /api/admin/usage` → `byDay`. The only two series the API returns. */
type UsageDay = { day: string; runs: number; costUsd: number }

const OperatorDashboard = () => {
  const [days, setDays] = useState(30)
  // Destructured INSIDE the component, never at module scope: a module-scope capture freezes
  // the locale at import time and keeps rendering the previous language after a switch, with
  // no type error and no failing test (PRD-27 F-3).
  const i18n = useI18n()
  const { formatCost, formatCount, formatDuration, formatList, formatNumber, formatTokens } = i18n
  const t = i18n.t.activity

  // The `enabled` argument on these three is the operator gate. Inside this component the
  // gate has already been passed, so they are unconditionally on.
  const usage = useUsage(days, true)
  const storage = useStorage(true)
  const runs = useRuns()
  const models = useModels(true)
  const tools = useTools(true)

  const totals = usage.data?.totals
  /*
   * Zero-filled before anything charts it. `GET /api/admin/usage` groups by day and returns
   * only the days that HAD runs, so a quarter with activity on four days comes back as four
   * rows — and a chart that plots them evenly draws four busy days in a row while its own
   * label says "over the last 90 days". Filling the gaps makes the horizontal axis mean
   * elapsed time, which is the only thing a reader can be assumed to think it means.
   */
  const byDay = fillDays(usage.data?.byDay ?? [], days)
  const successRate = totals && totals.runs > 0 ? (totals.successfulRuns / totals.runs) * 100 : null
  const costPerSuccess = totals && totals.successfulRuns > 0 ? totals.costUsd / totals.successfulRuns : null

  /*
   * A one-bar chart is a rectangle, not a trend, and a one-day comparison has no baseline to
   * compare against. Below two points the numbers themselves already say everything the
   * chart, the sparklines and the deltas would — so all three disappear together.
   */
  const trend = byDay.length > 1
  const half = Math.floor(days / 2)
  const runsDelta = trend ? changeOverHalf(byDay, (point) => point.runs, half) : null
  const costDelta = trend ? changeOverHalf(byDay, (point) => point.costUsd, half) : null

  const rows = runs.data?.items.slice(0, 25) ?? []
  const slowest = rows.reduce((longest, run) => Math.max(longest, runDuration(run) ?? 0), 0)

  return (
    <div className="fade-in min-h-0 flex-1 overflow-y-auto px-4 pb-[34px] pt-1 sm:px-6 lg:px-10">
      <div className="mx-auto w-full max-w-[992px]">
        <header className="flex flex-wrap items-end gap-x-4 gap-y-3 pb-5 pt-4">
          <div className="min-w-0">
            <h1 className="display text-[30px] sm:text-[38px]">{t.title}</h1>
            <p className="mt-[7px] max-w-[430px] text-pretty text-[13.5px] leading-[1.5] text-fg-muted">{t.lead}</p>
          </div>
          {/* The pills sit at the far right beside the title, and fall in under it on a phone. */}
          <div className="ml-auto flex gap-1.5 max-sm:ml-0">
            {RANGE_DAYS.map((range) => (
              <button
                key={range}
                type="button"
                aria-pressed={days === range}
                onClick={() => setDays(range)}
                className={clsx(
                  'h-[34px] rounded-full border px-3 font-mono text-[10px] tracking-[0.05em] sm:h-[29px]',
                  'transition-[color,border-color,transform,box-shadow] duration-200 ease-out',
                  days === range
                    ? 'border-rule-strong bg-card-solid text-fg'
                    : 'border-rule bg-card text-fg-muted hover:-translate-y-px hover:border-rule-strong hover:text-fg'
                )}
              >
                {t.rangeDays(i18n, range)}
              </button>
            ))}
          </div>
        </header>

        {/*
          The failure sits above the cards rather than replacing them. The four labels are
          what this screen measures, and a reader who arrives during an outage should still
          learn that — with every value reading "—" and one sentence saying why.
        */}
        {usage.isError && (
          <Panel className="mb-3">
            <PanelFailure what="usage" onRetry={() => void usage.refetch()} />
          </Panel>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            index={0}
            label={t.cards.runs}
            tone="primary"
            icon={<Activity size={12} strokeWidth={2.1} aria-hidden />}
            value={totals ? formatCount(totals.runs) : i18n.t.common.none}
            delta={runsDelta}
            deltaOver={half}
            series={byDay.map((point) => ({ key: point.day, value: point.runs }))}
            seriesLabel={t.runsPerDay(i18n, days)}
          />
          <MetricCard
            index={1}
            label={t.cards.taskSuccess}
            tone="grounded"
            icon={<CircleCheck size={12} strokeWidth={2.1} aria-hidden />}
            value={formatRate(i18n, successRate === null ? null : successRate / 100) ?? i18n.t.common.none}
            footnote={t.completedAndFailed(i18n, totals?.successfulRuns ?? 0, totals?.failedRuns ?? 0)}
          />
          <MetricCard
            index={2}
            label={t.cards.latency}
            tone="primary"
            icon={<Zap size={12} strokeWidth={2.1} aria-hidden />}
            value={totals ? formatDuration(totals.medianLatencyMs) : i18n.t.common.none}
            footnote={totals ? t.p95(formatDuration(totals.p95LatencyMs)) : undefined}
          />
          <MetricCard
            index={3}
            label={t.cards.spend}
            tone="alert"
            icon={<Coins size={12} strokeWidth={2.1} aria-hidden />}
            value={totals ? formatCost(totals.costUsd) : i18n.t.common.none}
            delta={costDelta}
            deltaOver={half}
            alertOnRise
            series={byDay.map((point) => ({ key: point.day, value: point.costUsd }))}
            seriesLabel={t.spendPerDay(i18n, days)}
            footnote={costPerSuccess === null ? undefined : t.perSuccessfulRun(formatCost(costPerSuccess))}
          />
        </div>

        {trend && <SpendChart data={byDay} />}

        {/*
          Spend by agent, then the four stores. Both sit above the run log because they are the
          two questions this screen is opened with once a deployment has more than one agent on
          it — "whose agent is this" and "how full is it getting" — and the log is what somebody
          reads after they know which one to look at.
        */}
        <AgentUsagePanel
          byAgent={usage.data?.byAgent}
          windowRuns={totals?.runs}
          loading={usage.isLoading}
          failed={usage.isError}
          days={days}
        />

        <StoragePanel
          storage={storage.data}
          loading={storage.isLoading}
          failed={storage.isError}
          onRetry={() => void storage.refetch()}
        />

        <Panel className="mt-3 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-rule px-4 py-3">
            <Clock size={12} strokeWidth={2.1} className="shrink-0 text-fg-muted" aria-hidden />
            <h2 className="eyebrow text-fg-muted">{t.recentRuns}</h2>
            <span className="readout ml-auto hidden items-center gap-1.5 text-fg-muted sm:flex">
              <RotateCcw size={10} strokeWidth={2.2} aria-hidden />
              {t.recentRunsHint}
            </span>
          </div>

          {runs.isError && <PanelFailure what="runs" onRetry={() => void runs.refetch()} />}
          {!runs.isError && runs.isLoading && <PanelBusy what="runs" />}
          {!runs.isError && !runs.isLoading && rows.length === 0 && <PanelNote>{t.noRuns}</PanelNote>}

          {rows.map((run, index) => (
            <RunRow key={run.id} run={run} slowest={slowest} index={index} />
          ))}
        </Panel>

        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          <section className="flex flex-col gap-3">
            <div>
              <h2 className="eyebrow mb-2">{t.byModel}</h2>
              <Panel className="divide-y divide-rule overflow-hidden">
                {usage.isError && (
                  <PanelFailure what="usageByModel" onRetry={() => void usage.refetch()} announce={false} />
                )}
                {!usage.isError && usage.isLoading && <PanelBusy what="usage" />}
                {usage.data?.byModel.length === 0 && <PanelNote>{t.noModelCalls}</PanelNote>}
                {usage.data?.byModel.map((row) => (
                  <div key={row.model} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-[550] tracking-[-0.014em]">{row.model}</span>
                      <span className="readout mt-0.5 block text-fg-subtle">
                        {t.callsCount(i18n, row.calls)} ·{' '}
                        {t.tokensInOut(formatTokens(row.inputTokens), formatTokens(row.outputTokens))}
                      </span>
                    </span>
                    <span className="readout shrink-0 font-medium">{formatCost(row.costUsd)}</span>
                  </div>
                ))}
              </Panel>
            </div>

            <div>
              <h2 className="eyebrow mb-2">{t.registeredModels}</h2>
              <Panel className="divide-y divide-rule overflow-hidden">
                {models.isError && <PanelFailure what="models" onRetry={() => void models.refetch()} />}
                {!models.isError && models.isLoading && <PanelBusy what="models" />}
                {models.data?.items.length === 0 && <PanelNote>{t.noModels}</PanelNote>}
                {models.data?.items.map((entry) => (
                  <div key={entry.alias} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="min-w-0">
                      <span className="block text-[13px] font-[550] tracking-[-0.014em]">
                        {entry.alias} → {entry.displayName}
                      </span>
                      <span className="readout mt-0.5 block text-fg-subtle">
                        {t.modelSpec(
                          formatNumber(entry.contextWindow / 1000, { maximumFractionDigits: 0 }),
                          entry.inputCostPerMTokUsd,
                          entry.outputCostPerMTokUsd
                        )}
                      </span>
                    </span>
                    <Badge>{entry.provider}</Badge>
                  </div>
                ))}
              </Panel>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h2 className="eyebrow mb-2">{t.tools}</h2>
              <Panel className="divide-y divide-rule overflow-hidden">
                {tools.isError && <PanelFailure what="tools" onRetry={() => void tools.refetch()} />}
                {!tools.isError && tools.isLoading && <PanelBusy what="tools" />}
                {tools.data?.items.length === 0 && <PanelNote>{t.noTools}</PanelNote>}
                {tools.data?.items.map((tool) => (
                  <div key={tool.name} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-[550] tracking-[-0.014em]">{tool.displayName}</span>
                      <span className="flex shrink-0 gap-1.5">
                        <Badge tone={tool.risk === 'read' ? 'neutral' : 'alert'}>{t.risk[tool.risk]}</Badge>
                        {tool.requiresApproval && <Badge tone="alert">{t.approvalBadge}</Badge>}
                      </span>
                    </div>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{tool.description}</p>
                    {/* The one list join in the repository, so the conjunction is the locale's
                        rather than a hardcoded comma (PRD-27 LOC-016). */}
                    {tool.dataLeavingPlatform.length > 0 && (
                      <p className="readout mt-1.5 text-alert">{t.dataReaches(formatList(tool.dataLeavingPlatform))}</p>
                    )}
                  </div>
                ))}
              </Panel>
            </div>

            <div>
              <h2 className="eyebrow mb-2">{t.topUsers}</h2>
              <Panel className="divide-y divide-rule overflow-hidden">
                {usage.isError && (
                  <PanelFailure what="usageByPerson" onRetry={() => void usage.refetch()} announce={false} />
                )}
                {!usage.isError && usage.isLoading && <PanelBusy what="usage" />}
                {usage.data?.topUsers.length === 0 && <PanelNote>{t.noActivity}</PanelNote>}
                {usage.data?.topUsers.map((row) => (
                  <div key={row.userId} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <span className="truncate text-[13px]">{row.displayName}</span>
                    <span className="readout shrink-0 text-fg-muted">
                      {t.runsCount(i18n, row.runs)} · {formatCost(row.costUsd)}
                    </span>
                  </div>
                ))}
              </Panel>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The run log
// ---------------------------------------------------------------------------

type RunListItem = {
  id: string
  conversationId: string
  conversationTitle: string
  status: string
  createdAt: string
  endedAt: string | null
  modelCalls: number
  toolCalls: number
  costUsd: number
}

/**
 * One run, as a full-width row.
 *
 * It stays an anchor rather than the reference design's `<button>`: this navigates, and a
 * link is the only element that can be opened in a new tab, middle-clicked or copied — all
 * of which an operator reading a log does constantly. The row *looks* like the comp's button
 * and behaves like the route it has always pointed at.
 *
 * The entrance stagger is capped at eight rows, so a hundred-row log does not spend four
 * seconds arriving one line at a time.
 */
const RunRow = ({ run, slowest, index }: { run: RunListItem; slowest: number; index: number }) => {
  const i18n = useI18n()
  const { formatCost, formatDuration } = i18n
  const t = i18n.t.activity
  const tone = statusTone(run.status)
  const Glyph = tone === 'grounded' ? CircleCheck : tone === 'alert' ? CircleSlash : Clock
  const duration = runDuration(run)
  const share = duration === null || slowest === 0 ? null : Math.max(3, (duration / slowest) * 100)

  return (
    <Link
      to={`/c/${run.conversationId}`}
      style={{ animationDelay: `${Math.min(index, 8) * 35}ms` }}
      className={clsx(
        // `focus-outline`: this row lives inside an `overflow-hidden` panel, which clips the
        // spread focus ring down to two hairlines indistinguishable from the row dividers
        // around it. The inset outline is not clipped.
        'focus-outline rise flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-rule px-4 py-[11px]',
        'text-fg transition-colors duration-200 last:border-b-0 hover:bg-card-solid'
      )}
    >
      <span className="readout w-[58px] shrink-0 text-fg-muted">{runClock(i18n, run.createdAt)}</span>

      {/*
        The tile is the row's one block of colour and the badge on the right is the word for
        the same fact. Neither stands alone: colour is never the only carrier of meaning
        (AGM-124), and a coloured square with no label is exactly that.
      */}
      <span
        style={{ boxShadow: 'inset 0 -1px 2px color-mix(in srgb, var(--shadow-ink) 22%, transparent)' }}
        className={clsx(
          'flex size-6 shrink-0 items-center justify-center rounded-[8px] text-shell-solid',
          tone === 'grounded' && 'bg-grounded',
          tone === 'alert' && 'bg-alert',
          tone === 'primary' && 'bg-primary'
        )}
      >
        <Glyph size={12} strokeWidth={2} aria-hidden />
      </span>

      {/*
        A floor of 9rem rather than `min-w-0`: it is what makes the row wrap on a phone
        instead of crushing the question down to two characters.
      */}
      <span className="min-w-[9rem] flex-1">
        <span className="block truncate text-[13px] font-[550] tracking-[-0.014em]">{run.conversationTitle}</span>
        <span className="readout mt-0.5 block text-[9.5px] text-fg-muted">
          {formatDuration(duration)} · {t.modelCalls(i18n, run.modelCalls)} · {t.toolCalls(i18n, run.toolCalls)}
        </span>
      </span>

      <span className="ml-auto flex items-center gap-3">
        {/*
          The comp's tape is split by phase, which lives on `GET /api/runs/:id` — one request
          per row. What the list itself carries is the run's wall-clock length, so the tape is
          one bar, proportional to the longest run on screen. Duration is stated in words in
          the line above, which is why this is decoration to a screen reader.
        */}
        <span className="hidden h-[5px] w-[88px] shrink-0 overflow-hidden rounded-[3px] bg-rule lg:block" aria-hidden>
          {share !== null && (
            <span
              style={{ width: `${share}%` }}
              className={clsx('block h-full rounded-[3px]', tone === 'alert' ? 'bg-alert' : 'bg-primary')}
            />
          )}
        </span>

        <Badge tone={tone} className="min-w-[86px] shrink-0 justify-center whitespace-nowrap">
          {runStatusLabel(t, run.status)}
        </Badge>

        <span className="readout w-[56px] shrink-0 text-right text-[10.5px]">{formatCost(run.costUsd)}</span>
      </span>
    </Link>
  )
}

const statusTone = (status: string): 'grounded' | 'alert' | 'primary' =>
  status === 'completed' ? 'grounded' : status === 'failed' ? 'alert' : 'primary'

/**
 * Wall-clock length of a run, computed here because `/api/runs` does not carry one.
 *
 * `createdAt → endedAt` includes whatever the run spent queued, so a row reads slightly
 * longer than the `medianLatencyMs` in the cards above, which the API measures from
 * `started_at`. Both are true and neither is invented; they answer different questions.
 * `null` for a run that has not ended — a duration for an unfinished run would be a guess.
 */
const runDuration = (run: { createdAt: string; endedAt: string | null }): number | null => {
  if (!run.endedAt) return null
  const ms = new Date(run.endedAt).getTime() - new Date(run.createdAt).getTime()
  return Number.isFinite(ms) && ms >= 0 ? ms : null
}

/**
 * The time column.
 *
 * Absolute rather than the `formatAgo` every other surface uses. This is a log, and a column
 * reading "2h ago / 3h ago / 1d ago" can be scanned for *how long ago* but not for *when*,
 * which is the question someone reconciling a spike actually has. Today keeps the clock, the
 * last week keeps the weekday, anything older falls back to the date.
 *
 * The three branches used to read the BROWSER's locale through bare `toLocale*` calls, which
 * is not the locale the product is being read in: a Brazilian reading the English build got a
 * 24-hour clock in an otherwise 12-hour screen. They take the active locale now.
 *
 * All three branches go through the shared formatters, so all three share the formatter cache
 * this column needs — it re-renders on a poll and constructs nothing per row.
 */
const runClock = (
  fmt: Pick<I18n, 'locale' | 'formatTime' | 'formatDayMonth' | 'formatWeekday'>,
  iso: string
): string => {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return '—'
  const now = new Date()
  if (at.toDateString() === now.toDateString()) return fmt.formatTime(at)
  if (now.getTime() - at.getTime() < 6 * 86_400_000) return fmt.formatWeekday(at)
  return fmt.formatDayMonth(at)
}

/**
 * `hooks/use-api.ts` types the run list's `status` as a bare `string` rather than the contract
 * enum `/api/runs` actually sends, so the exhaustive map in the catalogue cannot be indexed by
 * it directly. A value this client does not know means a client older than the server, and it
 * says so rather than printing the wire token at a reader — which is the leak the map exists
 * to close (PRD-27 D-9). The cast and this fallback both come out when that hook is narrowed.
 *
 * The map itself is `t.activity.runStatus` now, because a status is a word somebody reads and
 * in Portuguese every one of them is a participle agreeing with *execução*.
 */
const runStatusLabel = (t: I18n['t']['activity'], status: string): string =>
  t.runStatus[status as RunStatus] ?? t.unknownStatus

// ---------------------------------------------------------------------------
// Metric cards
// ---------------------------------------------------------------------------

type MetricCardProps = {
  label: string
  value: string
  icon: ReactNode
  tone: 'primary' | 'grounded' | 'alert'
  index: number
  /** Percentage change over the window's midpoint, or `null` when there is no baseline. */
  delta?: number | null
  /** How many days each half of that comparison covers — the delta is meaningless without it. */
  deltaOver?: number
  alertOnRise?: boolean
  series?: Array<{ key: string; value: number }>
  seriesLabel?: string
  footnote?: string
}

/**
 * One measured fact.
 *
 * The bottom of the card is pushed down with `mt-auto` so four cards of unequal content
 * still line their charts and footnotes up with each other — the grid already makes them the
 * same height, and without this the shorter ones float their last line into the middle.
 */
const MetricCard = ({
  label,
  value,
  icon,
  tone,
  index,
  delta,
  deltaOver,
  alertOnRise,
  series,
  seriesLabel,
  footnote
}: MetricCardProps) => (
  <div
    style={{ '--wash-accent': `var(--${tone})`, animationDelay: `${index * 50}ms` } as CSSProperties}
    className="card accent-wash rise flex flex-col rounded-card px-[15px] pb-[15px] pt-[14px]"
  >
    <div
      className={clsx(
        'flex items-center gap-1.5',
        tone === 'grounded' && 'text-grounded',
        tone === 'alert' && 'text-alert',
        tone === 'primary' && 'text-primary'
      )}
    >
      {icon}
      <span className="eyebrow tracking-[0.11em] text-fg-muted">{label}</span>
    </div>

    <div className="mt-[5px] flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <span className="font-mono text-[25px] font-medium tabular-nums tracking-[-0.028em]">{value}</span>
      {delta !== null && delta !== undefined && deltaOver !== undefined && (
        <Delta pct={delta} over={deltaOver} alertOnRise={alertOnRise} />
      )}
    </div>

    <div className="mt-auto pt-2.5">
      {series && series.length > 1 && seriesLabel && <Sparkline points={series} tone={tone} label={seriesLabel} />}
      {footnote && <p className="readout mt-1.5 text-fg-subtle">{footnote}</p>}
    </div>
  </div>
)

/**
 * The change arrow.
 *
 * Direction is carried by the glyph and the sign, not by the colour, so the card still reads
 * correctly in monochrome. Only spend is tinted: everywhere in this design `--alert` means
 * consequence, and rising cost is the consequence an operator is watching for.
 */
const Delta = ({ pct, over, alertOnRise }: { pct: number; over: number; alertOnRise?: boolean }) => {
  const i18n = useI18n()
  const { formatNumber } = i18n
  const t = i18n.t.activity
  const flat = Math.abs(pct) < 1
  const Glyph = flat ? Minus : pct > 0 ? TrendingUp : TrendingDown
  // The sign is the glyph's twin and stays ours; only the figure is the locale's.
  const label = flat ? t.steady : `${pct > 0 ? '+' : '-'}${formatNumber(Math.abs(pct), { maximumFractionDigits: 0 })}%`
  const tint = alertOnRise && !flat ? (pct > 0 ? 'text-alert' : 'text-grounded') : 'text-fg-muted'

  return (
    <span className={clsx('readout flex items-center gap-1', tint)}>
      <Glyph size={10} strokeWidth={2.4} aria-hidden />
      {label}
      {/* The leading space belongs to the view: an entry that ends or starts with one is a
          fragment waiting to be mis-trimmed (PRD-27 LOC-014). */}
      <span className="sr-only"> {t.againstPrevious(i18n, over)}</span>
    </span>
  )
}

/**
 * A series at a glance.
 *
 * Only ever rendered from real points — a metric whose series the API does not return loses
 * its sparkline and keeps its card. The bars fade toward the past so the shape reads as time
 * without needing an axis, and the summary sentence beside them is what a screen reader gets
 * instead of thirty unlabelled rectangles.
 */
const Sparkline = ({
  points,
  tone,
  label
}: {
  points: Array<{ key: string; value: number }>
  tone: 'primary' | 'grounded' | 'alert'
  label: string
}) => {
  const peak = Math.max(...points.map((point) => point.value), 0) || 1

  return (
    <>
      <span className="sr-only">{label}</span>
      <div aria-hidden className="flex h-[26px] items-end" style={{ gap: points.length > 30 ? 1 : 2 }}>
        {points.map((point, index) => (
          <span
            key={point.key}
            style={{
              height: `${Math.max(2, (point.value / peak) * 100)}%`,
              opacity: 0.32 + (0.68 * index) / (points.length - 1)
            }}
            className={clsx(
              'min-w-px flex-1 rounded-[2px]',
              tone === 'grounded' && 'bg-grounded',
              tone === 'alert' && 'bg-alert',
              tone === 'primary' && 'bg-primary'
            )}
          />
        ))}
      </div>
    </>
  )
}

/**
 * Change across the window's own midpoint: the most recent `half` days against the `half`
 * before them.
 *
 * Anchored on the last day the server reported rather than on the browser's clock. The day
 * keys are bucketed in the database's timezone, so a locally computed "today" puts every run
 * either side of midnight into the wrong half and the delta flickers by the hour.
 *
 * Returns `null` when the earlier half is empty. A percentage against a zero baseline is
 * either infinity or a fiction, and "+100%" on the first day of data is a claim about a trend
 * nobody can see yet.
 */
const changeOverHalf = (
  series: readonly UsageDay[],
  pick: (point: UsageDay) => number,
  half: number
): number | null => {
  const last = series.at(-1)?.day
  if (!last || half < 1) return null

  const recentFrom = shiftDay(last, -(half - 1))
  const earlierFrom = shiftDay(last, -(2 * half - 1))

  let recent = 0
  let earlier = 0
  for (const point of series) {
    if (point.day >= recentFrom) recent += pick(point)
    else if (point.day >= earlierFrom) earlier += pick(point)
  }

  return earlier === 0 ? null : ((recent - earlier) / earlier) * 100
}

/** Day arithmetic in UTC, so a DST boundary cannot move a bucket into the neighbouring day. */
/**
 * One entry per calendar day in the window, oldest first, zeroes where nothing ran.
 *
 * Anchored to the newest day the API returned rather than to today, so the last column is a
 * day that actually has data and the chart never ends in a run of empty space caused by the
 * request being a few minutes stale.
 */
const fillDays = (points: readonly UsageDay[], days: number): UsageDay[] => {
  if (points.length === 0) return []
  const byDay = new Map(points.map((point) => [point.day, point]))
  const last = points[points.length - 1]?.day
  if (!last) return [...points]

  const filled: UsageDay[] = []
  for (let offset = days - 1; offset >= 0; offset--) {
    const day = shiftDay(last, -offset)
    filled.push(byDay.get(day) ?? { day, runs: 0, costUsd: 0 })
  }
  // A point older than the window still belongs on the chart rather than being dropped.
  const earlier = points.filter((point) => point.day < (filled[0]?.day ?? ''))
  return [...earlier, ...filled]
}

const shiftDay = (day: string, by: number): string => {
  const at = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(at.getTime())) return day
  at.setUTCDate(at.getUTCDate() + by)
  return at.toISOString().slice(0, 10)
}

/**
 * Spend over time as a bare column chart.
 *
 * Deliberately unlabelled beyond the endpoints: at this size a full axis costs more space
 * than the extra precision is worth, and the exact figures are one hover away.
 */
const SpendChart = ({ data }: { data: readonly UsageDay[] }) => {
  const i18n = useI18n()
  const { formatCost } = i18n
  const t = i18n.t.activity
  const peak = Math.max(...data.map((point) => point.costUsd), 0.0001)

  return (
    <Panel className="mt-3 px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="eyebrow">{t.dailySpend}</h2>
        <span className="readout text-fg-subtle">{t.peak(formatCost(peak))}</span>
      </div>
      <div className="mt-3 flex h-20 items-end gap-[3px]">
        {data.map((point) => (
          <div
            key={point.day}
            title={t.dayTooltip(i18n, point.day, formatCost(point.costUsd), point.runs)}
            style={{ height: `${Math.max(2, (point.costUsd / peak) * 100)}%` }}
            className={clsx(
              // No minimum width: at 90 days a 3px floor plus gaps is ~540px, which is wider
              // than the whole stage on a phone and scrolls the page sideways. The height floor
              // below already guarantees every day leaves a visible mark.
              'min-w-0 max-w-[28px] flex-1 rounded-t-[3px] bg-primary/70',
              'transition-colors duration-200 hover:bg-primary'
            )}
          />
        ))}
      </div>
      <div className="mt-1.5 flex justify-between">
        <span className="readout text-fg-subtle">{data[0]?.day}</span>
        <span className="readout text-fg-subtle">{data.at(-1)?.day}</span>
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Panel states
// ---------------------------------------------------------------------------

/**
 * Which panel is waiting, and which one failed — a semantic key rather than a noun.
 *
 * Derived from the catalogue so the two cannot drift: adding a panel means adding its
 * sentence, and passing a key with no sentence behind it is a compile error.
 */
type PanelSubject = keyof I18n['t']['activity']['busy']
type FailureSubject = keyof I18n['t']['activity']['failure']

const PanelNote = ({ children }: { children: ReactNode }) => (
  <p className="px-4 py-3.5 text-[13px] leading-relaxed text-fg-subtle">{children}</p>
)

/**
 * The spinner is `aria-hidden`, so the words beside it are the whole announcement.
 *
 * `what` is a KEY, not a noun phrase. It used to be the English words themselves — "your
 * runs", "the model registry" — dropped into the middle of a sentence with their article
 * attached, which has no rendering at all in a language that inflects around them (PRD-27
 * F-10, D-20).
 */
const PanelBusy = ({ what }: { what: PanelSubject }) => {
  const t = useI18n().t.activity
  return (
    <p role="status" className="flex items-center gap-2 px-4 py-3.5 text-[13px] text-fg-subtle">
      <Spinner />
      {t.busy[what]}
    </p>
  )
}

/**
 * What a failed panel says.
 *
 * This screen had no error state at all: a request that never answered rendered the same
 * empty panel as a window with genuinely nothing in it, so an outage read as a quiet day.
 * Each panel now says which fetch failed and offers the only useful action, which is to ask
 * again — `refetch` on the query that broke, never a page reload.
 *
 * `announce` exists because one broken query can empty several panels. Only the first of
 * them takes `role="alert"`; three live regions firing for a single failed request talk over
 * each other and none of the three gets heard.
 */
const PanelFailure = ({
  what,
  onRetry,
  announce = true
}: {
  what: FailureSubject
  onRetry: () => void
  announce?: boolean
}) => {
  const t = useI18n().t
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
      <p role={announce ? 'alert' : undefined} className="min-w-0 flex-1 text-[13px] leading-relaxed text-alert">
        {t.activity.failure[what]}
      </p>
      <Button variant="mono" size="sm" onClick={onRetry}>
        {t.common.retry}
      </Button>
    </div>
  )
}
